import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, DollarSign, CheckCircle, XCircle, Zap } from "lucide-react";
import { API } from "@/config/api";

const AdminCovers_v2 = () => {
  const navigate = useNavigate();
  const [covers, setCovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCover, setSelectedCover] = useState(null);
  const [showRepaymentDialog, setShowRepaymentDialog] = useState(false);
  const [repaymentData, setRepaymentData] = useState({ amount: "", note: "" });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadCovers();
  }, []);

  const loadCovers = async () => {
    try {
      const response = await axios.get(`${API}/admin/covers`, {
        withCredentials: true
      });
      setCovers(response.data);
    } catch (error) {
      toast.error('Failed to load covers');
    } finally {
      setLoading(false);
    }
  };

  const handleLogRepayment = async () => {
    if (!repaymentData.amount) {
      toast.error('Please enter an amount');
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/cover/${selectedCover.cover_id}/repayment`,
        { 
          amount: parseFloat(repaymentData.amount),
          note: repaymentData.note
        },
        { withCredentials: true }
      );
      
      if (response.data.status === 'success') {
        toast.success('Repayment charged successfully!');
      } else {
        toast.error(`Charge failed: ${response.data.error_message}`);
      }
      
      setShowRepaymentDialog(false);
      setRepaymentData({ amount: "", note: "" });
      loadCovers();
    } catch (error) {
      toast.error('Failed to log repayment');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  const statusColors = {
    active: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    paid: "bg-green-500/20 text-green-400 border-green-500/30",
    defaulted: "bg-red-500/20 text-red-400 border-red-500/30"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-6">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-heading font-bold text-white">DueDay</span>
          </div>
          <span className="px-4 py-2 bg-yellow-400/20 border border-yellow-400/50 rounded-full text-yellow-400 text-sm font-semibold">
            Admin Panel
          </span>
        </div>
      </nav>

      <div className="container mx-auto max-w-7xl pt-16">
        <Button 
          data-testid="back-to-admin-btn"
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-6 rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Admin
        </Button>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold mb-2 text-white" data-testid="covers-title">
                Covers & Repayments
              </h1>
              <p className="text-slate-400">{covers.length} total covers</p>
            </div>
          </div>

          {covers.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No covers yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {covers.map((cover) => {
                const remaining = cover.amount_approved - cover.total_repaid;
                const percentPaid = (cover.total_repaid / cover.amount_approved) * 100;

                return (
                  <div 
                    key={cover.cover_id}
                    data-testid={`cover-${cover.cover_id}`}
                    className="p-6 border-2 border-slate-700 rounded-2xl hover:border-slate-600 transition-all bg-slate-900/50"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-heading font-bold text-white">
                            {cover.user?.name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[cover.status]}`}>
                            {cover.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mb-3">
                          {cover.user?.email} - Tier: <span className="capitalize text-slate-300">{cover.user?.membership_tier}</span>
                        </p>
                        <div className="bg-slate-800 p-4 rounded-xl mb-3 border border-slate-700">
                          <p className="text-sm font-medium mb-2 text-slate-300">Repayment Plan:</p>
                          <p className="text-sm text-slate-400">{cover.repayment_plan_text}</p>
                        </div>
                        <p className="text-xs text-slate-500">
                          Created: {new Date(cover.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right ml-6">
                        <div className="mb-4">
                          <p className="text-sm text-slate-400 mb-1">Amount Approved</p>
                          <p className="text-3xl font-heading font-bold text-yellow-400">
                            ${cover.amount_approved.toFixed(2)}
                          </p>
                        </div>
                        <div className="mb-4">
                          <p className="text-sm text-slate-400 mb-1">Total Repaid</p>
                          <p className="text-2xl font-heading font-semibold text-green-400">
                            ${cover.total_repaid.toFixed(2)}
                          </p>
                        </div>
                        <div className="mb-4">
                          <p className="text-sm text-slate-400 mb-1">Remaining</p>
                          <p className="text-xl font-heading font-semibold text-amber-400">
                            ${remaining.toFixed(2)}
                          </p>
                        </div>
                        {cover.status === 'active' && (
                          <Button
                            data-testid={`log-repayment-${cover.cover_id}-btn`}
                            onClick={() => {
                              setSelectedCover(cover);
                              setRepaymentData({ amount: remaining.toFixed(2), note: "" });
                              setShowRepaymentDialog(true);
                            }}
                            className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full w-full mt-2 font-bold"
                          >
                            Log Repayment
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-400">Repayment Progress</span>
                        <span className="font-semibold text-white">{percentPaid.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-3">
                        <div 
                          className="bg-green-500 rounded-full h-3 transition-all duration-300"
                          style={{ width: `${Math.min(percentPaid, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Repayment History */}
                    {cover.repayments && cover.repayments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <h4 className="text-lg font-heading font-semibold mb-3 text-white">Repayment History</h4>
                        <div className="space-y-2">
                          {cover.repayments.map((repayment) => (
                            <div 
                              key={repayment.repayment_id}
                              data-testid={`repayment-${repayment.repayment_id}`}
                              className="flex items-center justify-between p-3 bg-slate-800 rounded-lg text-sm border border-slate-700"
                            >
                              <div className="flex items-center gap-3">
                                {repayment.status === 'success' ? (
                                  <CheckCircle className="h-4 w-4 text-green-400" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-400" />
                                )}
                                <div>
                                  <p className="font-medium text-white">${repayment.amount.toFixed(2)}</p>
                                  <p className="text-xs text-slate-500">
                                    {new Date(repayment.date).toLocaleDateString()}
                                  </p>
                                  {repayment.note && (
                                    <p className="text-xs text-slate-400 mt-1">{repayment.note}</p>
                                  )}
                                  {repayment.error_message && (
                                    <p className="text-xs text-red-400 mt-1">{repayment.error_message}</p>
                                  )}
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                repayment.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                                {repayment.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Repayment Dialog */}
      <Dialog open={showRepaymentDialog} onOpenChange={setShowRepaymentDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="repayment-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Log Repayment</DialogTitle>
            <DialogDescription className="text-slate-400">
              Charge the member's card for a repayment. This will use Stripe to create a payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="repayment-amount" className="text-white" data-testid="repayment-amount-label">Amount to Charge</Label>
              <Input
                id="repayment-amount"
                data-testid="repayment-amount-input"
                type="number"
                step="0.01"
                placeholder="30.00"
                value={repaymentData.amount}
                onChange={(e) => setRepaymentData({...repaymentData, amount: e.target.value})}
                className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-yellow-400"
              />
            </div>
            <div>
              <Label htmlFor="repayment-note" className="text-white" data-testid="repayment-note-label">Note (Optional)</Label>
              <Textarea
                id="repayment-note"
                data-testid="repayment-note-textarea"
                placeholder="e.g., Weekly payment 1 of 5"
                value={repaymentData.note}
                onChange={(e) => setRepaymentData({...repaymentData, note: e.target.value})}
                className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-yellow-400"
              />
            </div>
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-300">
                This will charge the member's default payment method via Stripe. Make sure the amount is correct.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="confirm-repayment-btn"
                onClick={handleLogRepayment}
                disabled={actionLoading}
                className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full flex-1 font-bold"
              >
                {actionLoading ? 'Processing...' : 'Charge & Log Payment'}
              </Button>
              <Button
                data-testid="cancel-repayment-btn"
                onClick={() => setShowRepaymentDialog(false)}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700 rounded-full flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCovers_v2;
